import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Zap, Heart, ShieldCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo, { LOGO_SRC } from "@/components/Logo";

export default function Landing() {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [tiers, setTiers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/event/public").then((r) => setEvent(r.data)).catch(() => {});
    api.get("/event/pricing").then((r) => setTiers(r.data.tiers || [])).catch(() => {});
  }, []);

  const goPpv = () => {
    if (!user) navigate("/register");
    else if (!user.paid) navigate("/checkout");
    else navigate("/watch");
  };

  const lowestTier = tiers.length ? Math.min(...tiers.map((t) => t.amount)) : null;

  return (
    <div className="min-h-screen bg-[#050505] grain text-white overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo">
            <Logo size={32} />
          </Link>
          <nav className="flex items-center gap-3">
            {user ? (
              <>
                <Link
                  to="/watch"
                  className="btn-ghost px-4 py-2 text-xs hidden sm:inline-flex"
                  data-testid="nav-watch-btn"
                >
                  My Access
                </Link>
                {user.is_admin && (
                  <Link to="/admin" className="btn-ghost px-4 py-2 text-xs" data-testid="nav-admin-btn">
                    Admin
                  </Link>
                )}
              </>
            ) : (
              <Link to="/login" className="btn-ghost px-4 py-2 text-xs" data-testid="nav-login-btn">
                Sign in
              </Link>
            )}
            <button
              onClick={goPpv}
              className="btn-primary px-5 py-2 text-xs"
              data-testid="nav-cta-btn"
            >
              {lowestTier ? `Get PPV — from $${Math.round(lowestTier)}` : "Get PPV"}
            </button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen hero-grad flex items-center pt-24 pb-16">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow text-rose-500 mb-6"
              data-testid="hero-eyebrow"
            >
              Live PPV • In Macarthur
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-[16vw] md:text-[10vw] lg:text-[8.2rem] leading-[0.85] uppercase"
              data-testid="hero-title"
            >
              {event?.event_title || "Corporate Clash"}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-6 max-w-2xl text-lg text-zinc-300"
            >
              {event?.event_subtitle ||
                "Suit up. Glove up. One night of boxing where boardrooms meet the ring — every dollar fights for the cause."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={goPpv}
                className="btn-primary px-8 py-4 text-sm flex items-center gap-3"
                data-testid="hero-cta-btn"
              >
                {lowestTier ? `Get PPV Access — from $${Math.round(lowestTier)}` : "Get PPV Access"}
                <ArrowRight size={18} />
              </button>
              <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">
                Private stream • 1 device • Encrypted
              </span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 flex justify-center"
            data-testid="hero-logo"
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-rose-600/20 blur-3xl" />
              <img
                src={LOGO_SRC}
                alt="Corporate Clash in Macarthur"
                className="relative w-[280px] sm:w-[360px] lg:w-[440px] rounded-full ring-1 ring-white/10"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING TIERS */}
      <section id="pricing" className="border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-6">
              <p className="eyebrow text-rose-500">PPV Tiers</p>
              <h2 className="font-display text-6xl mt-3 uppercase">Pick your corner</h2>
            </div>
            <p className="md:col-span-6 text-zinc-400">
              Every tier unlocks the same live stream. Choose how generous you
              want to be — every extra dollar goes straight to the cause.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10" data-testid="tiers-grid">
            {tiers.map((t, i) => (
              <div
                key={t.id}
                className={`p-10 flex flex-col ${i === 1 ? "bg-rose-600" : "bg-[#0a0a0a]"}`}
                data-testid={`tier-${t.id}`}
              >
                <p className={`eyebrow ${i === 1 ? "text-white/80" : "text-zinc-500"}`}>{t.label}</p>
                <p className="font-display text-8xl mt-6 leading-none">
                  ${Math.round(t.amount)}
                </p>
                <p className={`mt-5 text-sm ${i === 1 ? "text-white/90" : "text-zinc-400"}`}>
                  {t.tagline}
                </p>
                <button
                  onClick={goPpv}
                  className={`mt-12 w-full py-4 text-xs uppercase tracking-[0.18em] font-bold transition-colors ${
                    i === 1
                      ? "bg-black text-white hover:bg-zinc-900"
                      : "btn-primary"
                  }`}
                  data-testid={`tier-cta-${t.id}`}
                >
                  Choose ${Math.round(t.amount)}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY / CHARITY */}
      <section className="bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-3 gap-10">
          {[
            {
              id: "charity",
              icon: Heart,
              title: "100% For The Cause",
              text: "Every PPV ticket directly funds the charity behind Corporate Clash in Macarthur.",
            },
            {
              id: "paywall",
              icon: Lock,
              title: "Private Paywall",
              text: "Your stream URL never leaves the server. Only authenticated paid accounts can decode it.",
            },
            {
              id: "single-device",
              icon: ShieldCheck,
              title: "1 Device At A Time",
              text: "Single-session enforcement. Log in elsewhere and your previous device is kicked instantly.",
            },
          ].map((f, i) => (
            <div key={f.id} className="card-flat p-8" data-testid={`feature-card-${i}`}>
              <f.icon size={28} className="text-rose-500" />
              <h3 className="font-display text-3xl mt-6 uppercase">{f.title}</h3>
              <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-white/10 bg-rose-600">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <img
              src={LOGO_SRC}
              alt="Corporate Clash"
              className="w-20 h-20 rounded-full ring-2 ring-black/30"
            />
            <h3 className="font-display text-5xl md:text-6xl uppercase max-w-xl leading-none">
              Don&apos;t miss a single round.
            </h3>
          </div>
          <button
            onClick={goPpv}
            className="bg-black text-white px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold hover:bg-zinc-900 transition-colors flex items-center gap-2"
            data-testid="footer-cta-btn"
          >
            <Zap size={14} /> Secure My PPV Access
          </button>
        </div>
      </section>

      <footer className="bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex items-center justify-between text-xs text-zinc-500">
          <Logo size={24} />
          <span className="uppercase tracking-[0.3em]">For the cause</span>
          <span>© {new Date().getFullYear()} Corporate Clash</span>
        </div>
      </footer>
    </div>
  );
}
